import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleSchema from '@/components/base/ArticleSchema';
import SEOSchema from '@/components/base/SEOSchema';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 text-foreground-950">
        <Navbar />
        <main className="pt-20 pb-10">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8 text-center py-20">
            <h1 className="text-2xl font-bold text-foreground-950">文章不存在</h1>
            <p className="mt-2 text-foreground-500">该爆料可能已被删除或链接失效</p>
            <Link to="/" className="mt-6 inline-block px-6 py-3 bg-accent-500 text-white rounded-full font-semibold hover:bg-accent-600 transition-colors">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <SEOSchema type="Organization" />
      <SEOSchema type="WebSite" />
      <SEOSchema
        type="WebPage"
        data={{
          name: article.title,
          url: `https://heiliaobaoliao.com/article/${article.id}`,
        }}
      />
      <ArticleSchema article={article} />

      <div className="min-h-screen bg-background-50 text-foreground-950">
        <Navbar />

        <main className="pt-20 pb-10 md:pb-14">
          <article className="w-full max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
            {/* Breadcrumb */}
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: article.category, href: `/category/${article.categorySlug}` },
                  { label: article.title },
                ]}
              />
            </div>

            {/* Article Header */}
            <header className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="text-xs text-accent-500 font-medium bg-accent-500/10 px-3 py-1 rounded-full hover:bg-accent-500/20 transition-colors"
                >
                  {article.category}
                </Link>
                {article.isExclusive && (
                  <span className="text-xs bg-accent-500 text-white px-3 py-1 rounded-full font-bold">
                    独家
                  </span>
                )}
                {article.isHot && (
                  <span className="text-xs bg-primary-500 text-background-50 px-3 py-1 rounded-full font-bold">
                    热门
                  </span>
                )}
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-black text-foreground-950 leading-tight">
                {article.title}
              </h1>

              <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line"></i>
                  {article.viewCount >= 10000 ? `${(article.viewCount / 10000).toFixed(1)}万阅读` : `${article.viewCount}阅读`}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-message-2-line"></i>
                  {article.commentCount} 评论
                </span>
              </div>
            </header>

            {/* Featured Image */}
            <div className="mb-8 rounded-lg overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.imageAlt}
                className="w-full aspect-[16/9] object-cover"
              />
            </div>

            {/* Article Content */}
            <div
              className="prose prose-invert max-w-none
                prose-headings:text-foreground-950
                prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:text-base prose-p:my-4
                prose-strong:text-foreground-950
                prose-a:text-accent-500
                [&_p]:text-base [&_p]:leading-loose [&_p]:text-foreground-700 [&_p]:mb-5"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            {article.tags.length > 0 && (
              <div className="mt-8 pt-6 border-t border-background-200/50">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-foreground-500">标签：</span>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-sm text-foreground-600 bg-background-100 border border-background-200/50 px-3 py-1 rounded-full"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Share */}
            <div className="mt-6 flex items-center gap-3">
              <span className="text-sm text-foreground-500">分享到：</span>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 border border-background-200/50 text-foreground-600 hover:text-accent-500 hover:border-accent-500/30 transition-colors" aria-label="分享到微博">
                <i className="ri-weibo-line"></i>
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 border border-background-200/50 text-foreground-600 hover:text-accent-500 hover:border-accent-500/30 transition-colors" aria-label="分享到微信">
                <i className="ri-wechat-line"></i>
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 border border-background-200/50 text-foreground-600 hover:text-accent-500 hover:border-accent-500/30 transition-colors" aria-label="复制链接">
                <i className="ri-link"></i>
              </button>
            </div>

            {/* Disclaimer */}
            <div className="mt-8 p-4 bg-background-100 border border-background-200/50 rounded-lg">
              <p className="text-xs text-foreground-500 leading-relaxed">
                <strong className="text-foreground-700">免责声明：</strong>
                本文内容来自网友投稿或网络整理，不代表本站立场。如涉及侵权或不当内容，请通过关于我们页面的联系方式与我们取得联系，我们将及时处理。
              </p>
            </div>
          </article>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="w-full max-w-4xl mx-auto px-4 md:px-6 lg:px-8 mt-12">
              <div className="border-t border-background-200/50 pt-10">
                <h2 className="text-xl font-bold text-foreground-950 mb-6">
                  相关爆料
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {relatedArticles.map((rel) => (
                    <ArticleCard key={rel.id} article={rel} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}